import { formatReportDateShort } from '../../utils/cropCalendarReport';
import type { CropCalendarReportModel, HarvestCompareKind } from '../../utils/cropCalendarReport';

function parseIso(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function confidenceDots(howSure: string): { filled: number; label: string } {
  const s = howSure.toLowerCase();
  if (/high|very|strong/.test(s)) return { filled: 3, label: howSure };
  if (/fair|moderate|mid/.test(s)) return { filled: 2, label: howSure };
  if (/low|limited|uncertain/.test(s)) return { filled: 1, label: howSure };
  return { filled: 2, label: howSure };
}

export function CropCalendarConfidenceMeter({
  howSure,
  isDark,
}: {
  howSure: string;
  isDark: boolean;
}) {
  if (!howSure.trim()) return null;
  const { filled, label } = confidenceDots(howSure);
  const dotOn = isDark ? 'bg-[#34d399]' : 'bg-[#008540]';
  const dotOff = isDark ? 'bg-white/20' : 'bg-[#e5e7eb]';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        isDark ? 'bg-[#1a3324] text-[#34d399]' : 'bg-[#e3f2e8] text-[#008540]'
      }`}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`inline-block h-2 w-2 rounded-full ${i < filled ? dotOn : dotOff}`}
          aria-hidden
        />
      ))}
      {label}
    </span>
  );
}

export function CropCalendarSeasonRail({
  periods,
  isDark,
}: {
  periods: CropCalendarReportModel['stagePeriods'];
  isDark: boolean;
}) {
  if (!periods.length) return null;

  const pick = periods.length > 6 ? periods.slice(-6) : periods;
  const currentIdx = pick.findIndex((p) => p.isCurrent);

  return (
    <div className="mt-3">
      <div className="flex gap-0 overflow-x-auto">
        {pick.map((p, i) => {
          const done = currentIdx >= 0 && i < currentIdx;
          const current = p.isCurrent;
          const segmentClass = done
            ? isDark
              ? 'bg-[#34d399]'
              : 'bg-[#008540]'
            : current
              ? isDark
                ? 'bg-[#34d399]/55'
                : 'bg-[#008540]/55'
              : isDark
                ? 'bg-[#2d3748]'
                : 'bg-[#e5e7eb]';
          return (
            <span
              key={`${p.stage}-${i}`}
              className={`h-2 min-w-[2.5rem] flex-1 first:rounded-l-lg last:rounded-r-lg ${segmentClass}`}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between gap-1 text-[11.5px] text-gray-500 dark:text-[#9ca3af]">
        {pick.map((p) => (
          <span key={p.stage} className="min-w-0 flex-1 truncate text-center first:text-left last:text-right">
            {p.isCurrent ? (
              <strong className={isDark ? 'text-[#34d399]' : 'text-[#008540]'}>Now</strong>
            ) : (
              p.stage.split(' ')[0]
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function harvestBadgeClass(kind: HarvestCompareKind, isDark: boolean): string {
  if (kind === 'on_track') {
    return isDark ? 'bg-[#1a3324] text-[#34d399]' : 'bg-[#e3f2e8] text-[#008540]';
  }
  if (kind === 'earlier') {
    return isDark ? 'bg-[#3a2c0e] text-[#f0b64a]' : 'bg-[#fff1d6] text-[#9a5b00]';
  }
  if (kind === 'later') {
    return isDark ? 'bg-[#142a44] text-[#7ab4f2]' : 'bg-[#e1edfb] text-[#1d5fa8]';
  }
  return isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-700';
}

export function CropCalendarHarvestGauge({
  report,
  isDark,
}: {
  report: CropCalendarReportModel;
  isDark: boolean;
}) {
  const harvest = report.harvest;
  if (harvest.kind === 'none') return null;

  const predicted = parseIso(report.predictedHarvestDate);
  const expected = parseIso(report.expectedHarvestDate);
  const anchor = predicted || expected;
  if (!anchor) return null;

  const windowStart = new Date(anchor);
  windowStart.setDate(windowStart.getDate() - 14);
  const windowEnd = new Date(anchor);
  windowEnd.setDate(windowEnd.getDate() + 14);
  const span = windowEnd.getTime() - windowStart.getTime();

  const xFor = (d: Date | null) => {
    if (!d || span <= 0) return null;
    const pct = (d.getTime() - windowStart.getTime()) / span;
    return Math.max(10, Math.min(330, 10 + pct * 320));
  };

  const predX = xFor(predicted);
  const expX = xFor(expected);
  const bandLeft = xFor(new Date(anchor.getTime() - 7 * 86400000)) ?? 108;
  const bandRight = xFor(new Date(anchor.getTime() + 7 * 86400000)) ?? 258;
  const bandW = Math.max(20, bandRight - bandLeft);
  const chartColor = isDark ? '#34d399' : '#008540';
  const lineColor = isDark ? '#2d3748' : '#e5e7eb';

  return (
    <div>
      <div className="my-2 grid grid-cols-2 gap-2">
        <div className={`rounded-xl border px-2.5 py-2 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
          <span className={`block text-xs ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>Your plan</span>
          <b className={`block text-xl tabular-nums ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
            {harvest.expectedLabel || '—'}
          </b>
        </div>
        <div className={`rounded-xl border px-2.5 py-2 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
          <span className={`block text-xs ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>Field estimate</span>
          <b className={`block text-xl tabular-nums ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
            {harvest.predictedLabel || '—'}
          </b>
        </div>
      </div>
      {(predX != null || expX != null) && (
        <svg viewBox="0 0 340 46" width="100%" role="img" aria-label={harvest.detail} className="my-1">
          <line x1="10" x2="330" y1="24" y2="24" stroke={lineColor} strokeWidth="4" />
          {harvest.kind === 'on_track' && (
            <rect x={bandLeft} y="14" width={bandW} height="20" rx="10" fill={chartColor} opacity="0.18" />
          )}
          {predX != null && <circle cx={predX} cy="24" r="8" fill={chartColor} />}
          {expX != null && (
            <circle cx={expX} cy="24" r="8" fill={isDark ? '#1c2432' : '#fff'} stroke={chartColor} strokeWidth="3" />
          )}
          {predicted && (
            <text x={Math.max(8, (predX ?? 150) - 20)} y="9" fill={isDark ? '#9ca3af' : '#6b7280'} fontSize="10">
              {formatReportDateShort(report.predictedHarvestDate)}
            </text>
          )}
          {expected && expX != null && (
            <text x={Math.max(8, expX - 10)} y="44" fill={isDark ? '#9ca3af' : '#6b7280'} fontSize="10">
              {formatReportDateShort(report.expectedHarvestDate)}
            </text>
          )}
        </svg>
      )}
      <span className={`mt-1 inline-block rounded-lg px-2.5 py-0.5 text-xs font-bold ${harvestBadgeClass(harvest.kind, isDark)}`}>
        {harvest.headline}
      </span>
      <p className={`mt-2 text-sm leading-relaxed ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
        {harvest.detail}
      </p>
    </div>
  );
}

export function CropCalendarGreennessChart({
  rows,
  stagePeriods,
  endDate,
  isDark,
}: {
  rows: CropCalendarReportModel['greennessRows'];
  stagePeriods: CropCalendarReportModel['stagePeriods'];
  endDate: string;
  isDark: boolean;
}) {
  if (rows.length < 2) return null;

  const w = 340;
  const h = 120;
  const plotTop = 12;
  const plotBottom = h - 22;
  const pts = rows.map((r) => r.ndvi);
  const px = (i: number) => 20 + i * (300 / Math.max(pts.length - 1, 1));
  const py = (v: number) => plotBottom - v * (plotBottom - plotTop);
  const line = pts.map((v, i) => `${px(i)},${py(v)}`).join(' ');
  const chartColor = isDark ? '#34d399' : '#008540';
  const muted = isDark ? '#9ca3af' : '#6b7280';
  const cloudy = new Set(
    rows.map((r, i) => (r.cloudPct != null && r.cloudPct > 30 ? i : -1)).filter((i) => i >= 0)
  );

  const bandCount = Math.min(4, Math.max(1, stagePeriods.filter((p) => p.stage).length));
  const showBands = pts.length >= 6 && bandCount >= 2;
  const bands: Array<[number, number]> = [];
  if (showBands) {
    for (let i = 0; i < bandCount; i += 1) {
      const a = Math.floor((i / bandCount) * (pts.length - 1));
      const b = Math.ceil(((i + 1) / bandCount) * (pts.length - 1));
      if (px(b) - px(a) >= 36) bands.push([a, b]);
    }
  }

  const sparse = pts.length < 6;
  const summary = `Greenness from ${rows[0]?.date} to ${rows[rows.length - 1]?.date}`;
  const lastX = px(pts.length - 1);
  const endLabelX = Math.max(14, Math.min(lastX - 8, w - 52));

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label={summary}>
        {bands.map(([a, b], i) => (
          <rect
            key={`band-${a}-${b}`}
            x={px(a)}
            y={plotTop}
            width={Math.max(0, px(b) - px(a))}
            height={plotBottom - plotTop}
            fill={chartColor}
            opacity={i % 2 ? 0.05 : 0.09}
          />
        ))}
        <polygon
          points={`${px(0)},${plotBottom} ${line} ${px(pts.length - 1)},${plotBottom}`}
          fill={chartColor}
          opacity="0.15"
        />
        <polyline points={line} fill="none" stroke={chartColor} strokeWidth="2.5" strokeLinejoin="round" />
        {pts.map((v, i) => (
          <circle
            key={i}
            cx={px(i)}
            cy={py(v)}
            r={sparse ? 5 : 4}
            fill={cloudy.has(i) ? (isDark ? '#1c2432' : '#fff') : chartColor}
            stroke={chartColor}
            strokeWidth="2"
          />
        ))}
        {endDate && pts.length > 1 && (
          <line
            x1={lastX}
            x2={lastX}
            y1={plotTop}
            y2={plotBottom}
            stroke={isDark ? '#e6edf3' : '#111827'}
            strokeDasharray="3 3"
            opacity="0.45"
          />
        )}
        <text x={14} y={h - 5} fill={muted} fontSize="10">
          {formatReportDateShort(rows[0]?.date)}
        </text>
        {pts.length > 2 && (
          <text
            x={px(Math.floor((pts.length - 1) / 2)) - 14}
            y={h - 5}
            fill={muted}
            fontSize="10"
            opacity="0.85"
          >
            {formatReportDateShort(rows[Math.floor((pts.length - 1) / 2)]?.date)}
          </text>
        )}
        <text x={endLabelX} y={h - 5} fill={muted} fontSize="10">
          {formatReportDateShort(endDate || rows[rows.length - 1]?.date)}
        </text>
      </svg>
      <div className={`mt-1 flex flex-wrap justify-between gap-1 text-[11.5px] ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
        <span>{cloudy.size > 0 ? '○ hollow = cloudy day' : '● each point = a clear field view'}</span>
        <span>Greener = healthier cover</span>
      </div>
      {sparse && (
        <p className={`mt-1.5 text-[11.5px] leading-snug ${isDark ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
          Only {pts.length} clear views so far — the line will fill in as more satellite passes arrive.
        </p>
      )}
    </div>
  );
}
